import { useHistory, useParams } from 'react-router-dom'

import type { PageParams } from '../../../../routes'
import { categorySlugToAkeneoMap } from '../../../../shared/constants/category-slug-to-akeneo-code-map'
import type { FilterCallback } from '../../../../shared/types/ReviewsFilters'
import { get } from '../../../../shared/utils/get'
import { merge } from '../../../../shared/utils/merge'
import { set } from '../../../../shared/utils/set'
import { updateUrlQueryParams } from '../../../../shared/utils/update-url-query-params'
import { getCleanedFilterData } from '../components/Filters'
import { getQueryValues, updateFilter } from '../components/Filters/utils'
import { updateNestedValue } from '../utils/update-nested-value'
import { useFiltersStates } from './useFiltersStates'
import { useUpdateFiltersHistory } from './useUpdateFiltersHistory'

export const useProductListingPageFilters = () => {
  const { categorySlug } = useParams<PageParams>()
  const { updateFilterHistory } = useUpdateFiltersHistory()
  const history = useHistory()

  const {
    filterDataMap,
    setFilterDataMap,
    filtersSnapshotMap,
    handleNeedsBasedFilters,
    setPreviousNeedsBasedFilterLabels,
    needsBasedFiltersOptionsMap,
    setNeedsBasedFiltersOptionsMap,
    setIsOverlayFiltersDisplayed,
    isOverlayFiltersDisplayed,
    showMoreFilters,
    setShowMoreFilters,
    filtersToRemoveMap,
    setFiltersToRemoveMap,
    selectedNeedsBasedFilters,
    pageNumberOnFilterInteraction,
    setPageNumberOnFilterInteraction,
  } = useFiltersStates()

  const filterCallback = ({
    path = '',
    filterValue,
    reset,
    revertToSnapshot,
    questionText = '',
    nbfLabel = '',
    options = [],
    individualFilters,
    isNbfSelected,
  }: FilterCallback) => {
    let {
      basic: clonedBasicFilterData,
      needsBased: clonedNeedsBasedFilterData,
      needsBasedQueryValues: clonedNbfForQueryValues,
    } = structuredClone(filterDataMap)

    setPageNumberOnFilterInteraction(1)
    const updatedUrl = updateUrlQueryParams({ page: '' })
    updatedUrl && history.replace(updatedUrl, null)

    switch (true) {
      case !!options.length: {
        /* v8 ignore else */
        if (individualFilters) {
          const { updatedBasicFilterData, updatedNeedsBasedFilterData, updatedNbfForQueryValues } =
            handleNeedsBasedFilters({
              options,
              questionText,
              individualFilters,
              label: nbfLabel,
              isSelected: isNbfSelected,
            })

          clonedBasicFilterData = updatedBasicFilterData
          clonedNeedsBasedFilterData = updatedNeedsBasedFilterData
          clonedNbfForQueryValues = updatedNbfForQueryValues
        }
        break
      }
      case revertToSnapshot: {
        clonedBasicFilterData = merge(
          structuredClone(filtersSnapshotMap.basic),
          filtersToRemoveMap.basic
        )
        clonedNeedsBasedFilterData = merge(
          structuredClone(filtersSnapshotMap.needsBased),
          filtersToRemoveMap.needsBased
        )
        clonedNbfForQueryValues = merge(
          structuredClone(filtersSnapshotMap.needsBasedQueryValues),
          filtersToRemoveMap.needsBasedQueryValues
        )

        setNeedsBasedFiltersOptionsMap({
          ...needsBasedFiltersOptionsMap,
          currentOptions: filtersSnapshotMap.currentNeedsBasedOptions,
        })
        break
      }
      case reset: {
        updateNestedValue(clonedBasicFilterData)
        updateNestedValue(clonedNeedsBasedFilterData)
        updateNestedValue(clonedNbfForQueryValues)
        setPreviousNeedsBasedFilterLabels({})
        setNeedsBasedFiltersOptionsMap({
          ...needsBasedFiltersOptionsMap,
          previousOptions: {},
        })
        break
      }
      case !!questionText: {
        const currentNbfFilter = get(filterDataMap.needsBased, path, []) as string[]
        const newNbfFilter = updateFilter({ filter: currentNbfFilter, filterValue })
        set(clonedNeedsBasedFilterData, path, newNbfFilter)
        set(clonedNbfForQueryValues, `${nbfLabel}.${path}`, newNbfFilter)
        break
      }
      default: {
        const currentFilter = get(filterDataMap.basic, path, []) as string[]
        const newFilter = updateFilter({ filter: currentFilter, filterValue })
        set(clonedBasicFilterData, path, newFilter)
        isOverlayFiltersDisplayed &&
          setFiltersToRemoveMap({
            ...filtersToRemoveMap,
            basic: set(structuredClone(filtersToRemoveMap.basic), path, []),
          })
        break
      }
    }

    const filterQueryValues = getQueryValues({ data: clonedBasicFilterData })
    const nbfFilterQueryValues = getQueryValues({
      data: clonedNbfForQueryValues,
      nbfLabel: 'nbf',
    })

    const queryValues = merge(structuredClone(filterQueryValues), nbfFilterQueryValues)
    const hasQueryValue = Object.values(queryValues).some((value) => value)
    const akeneoCode = categorySlugToAkeneoMap[categorySlug]
    queryValues['filter.cat'] = hasQueryValue && akeneoCode ? akeneoCode : ''

    updateFilterHistory(queryValues)

    setFilterDataMap({
      basic: getCleanedFilterData(clonedBasicFilterData),
      needsBased: getCleanedFilterData(clonedNeedsBasedFilterData),
      needsBasedQueryValues: clonedNbfForQueryValues,
    })
  }

  return {
    filterCallback,
    setIsOverlayFiltersDisplayed,
    isOverlayFiltersDisplayed,
    showMoreFilters,
    setShowMoreFilters,
    basicFilterData: filterDataMap.basic,
    needsBasedFilterData: filterDataMap.needsBased,
    selectedNeedsBasedFilters,
    pageNumberOnFilterInteraction,
    setPageNumberOnFilterInteraction,
  }
}
